<template>
  <a class="cso-info-icon" :data-content="content" :data-wide="wide" tabindex="0" ref="icon" aria-label="Weitere Informationen anzeigen"></a>
</template>

<script lang="ts">
const wrapperTemplate = `
  <div class="cso-info-wrapper" tabindex="-1">
    <div class="icon-tick"></div>
    <div class="icon-close" tabindex="0" aria-label="Schließen" role="img"></div>
    <div class="content"></div>
  </div>
  <div class="cso-info-overlay"></div>
`;

let currentIcon: HTMLElement | null = null

export default {
  name: 'CsoInfoIcon',
  props: {
    content: {
      type: String,
      required: true
    },
    wide: {
      type: Boolean,
      default: false
    }
  }
}

export const plugin = {
  getWrapper () {
    return document.querySelector('.cso-info-wrapper') as HTMLElement
  },
  open (icon: HTMLElement, hover = false) {
    const root = document.querySelector('html')!
    const wrapper = this.getWrapper()
    const iconRect = icon.getBoundingClientRect()
    const tick = wrapper.querySelector('.icon-tick') as HTMLElement
    wrapper.classList.add('open')
    wrapper.querySelector('.content')!.innerHTML = icon.dataset.content!
    if (icon.dataset.wide === 'true') {
      wrapper.classList.add('wide')
    } else {
      wrapper.classList.remove('wide')
    }
    if (hover) {
      wrapper.classList.add('hover')
    } else {
      wrapper.classList.remove('hover')
    }
    if (window.innerWidth > 790 && iconRect.top > 290) {
      // wrapper above the icon
      tick.style.top = ''
      tick.style.bottom = '-5px'
      wrapper.style.top = ''
      wrapper.style.bottom = (root.clientHeight - iconRect.top - window.scrollY + 8) + 'px'
      wrapper.style.right = (root.clientWidth - iconRect.right - window.scrollX + iconRect.width/2 - 25) + 'px'
    } else if (window.innerWidth > 790 && iconRect.top <= 290) {
      // wrapper below the icon
      tick.style.top = '-5px'
      tick.style.bottom = ''
      wrapper.style.top = iconRect.bottom + window.scrollY + 8 + 'px';
      wrapper.style.bottom = ''
      wrapper.style.right = (root.clientWidth - iconRect.right - window.scrollX + iconRect.width/2 - 25) + 'px'
    } else {
      // wrapper fullscreen
      wrapper.style.top = ''
      wrapper.style.bottom = ''
      wrapper.style.right = ''
    }
    wrapper.focus()
    currentIcon = icon
  },
  close () {
    this.getWrapper().classList.remove('open')
    currentIcon?.focus()
    currentIcon = null
  },
  handleClick (event: MouseEvent | KeyboardEvent) {
    const wrapper = this.getWrapper()
    const target = event.target as HTMLElement
    if (event.type == 'mouseover' && target.parentElement?.classList.contains('cso-info-text') && window.innerWidth > 790) {
      // hover on text parent on desktop
      this.open(target.parentElement, true)
      return
    }
    if (event.type == 'mouseleave' && target.parentElement?.classList.contains('cso-info-text') && window.innerWidth > 790) {
      // hover on text parent on desktop lost
      this.close()
      return
    }
    if (event.type != 'click' && (event as KeyboardEvent).code != 'Enter') {
      // other event
      return
    }
    if (target.classList.contains('cso-info-icon')) {
      // click on icon
      event.preventDefault()
      this.open(target)
    } else if (target.parentElement?.classList.contains('cso-info-text')) {
      // click on text parent
      event.preventDefault()
      this.open(target.parentElement)
    } else if (target.classList.contains('icon-close')) {
      // click on close
      this.close()
    } else if (target != wrapper && target.parentElement != wrapper) {
      // click outside of wrapper
      this.close()
    }
  },
  install () {
    document.body.insertAdjacentHTML('beforeend', wrapperTemplate)
    document.addEventListener('click', event => this.handleClick(event))
    document.addEventListener('keydown', event => this.handleClick(event))
    document.addEventListener('mouseover', event => this.handleClick(event))
    document.addEventListener('mouseleave', event => this.handleClick(event), true)
  }
}
</script>

<style lang="scss">
.cso-info-icon {
  cursor: pointer;

  &:before {
    font-family: vfde-icons;
    content: "\EA24";
    line-height: 1.125;
    font-size: 24px;
    vertical-align: middle;
    display: inline-block;
  }

  &:focus {
    outline: none !important;
  }

  &:focus-visible:before {
    outline: 2px solid $blue;
  }
}

.cso-info-overlay {
  display: none;
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: #000000a5;
  z-index: 100;

  @include sm {
    display: unset;
  }
}

.cso-info-wrapper {
  position: absolute;
  user-select: none;
  max-width: 358px;
  box-shadow: 0 2px 16px -3px rgb(0 0 0 / 22%);
  background: #fff;
  color: #000;
  padding: 20px 48px 24px 24px;
  z-index: 999;
  border-radius: 8px;

  &:focus {
    outline: none;
  }

  &.wide {
    max-width: 480px;
  }

  &.hover {
    max-width: 300px;
    padding: 20px 24px 24px 24px;
  }

  @include sm {
    max-width: unset !important;
    border-radius: unset;
    margin: 10px;
    position: fixed;
    bottom: 50%;
    transform: translateY(+50%);
    width: calc(100% - 20px);
  }

  &:not(.open),
  &:not(.open) + .cso-info-overlay {
    display: none;
  }

  &:not(.hover) .icon-close {
    position: absolute;
    right: 14px;
    top: 14px;
    background-image: url(/static/icons/close-big.svg);
    background-size: cover;
    cursor: pointer;
    width: 22px;
    height: 22px;

    &:focus-visible {
      outline: 2px solid $blue;
      outline-offset: 2px;
    }
  }

  .icon-tick {
    position: absolute;
    right: 19px;
    background-color: #fff;
    width: 10px;
    height: 10px;
    transform: rotate(45deg);

    @include sm {
      display: none;
    }
  }
}
</style>
